import { Controller } from "@hotwired/stimulus";

// Stimulus port of the legacy step2_seating.js (app/assets/javascripts) --
// desktop / flygcity-mobile-cutover only. aob/flysmarter mobile (not cut
// over, see mobile_redesign_plan memory) keeps that jQuery file completely
// unchanged; its markup (jquery-mobile <table> grid) has no data-action
// wiring for this controller at all, so it's simply never instantiated
// there.
//
// Attached to whatever wraps the whole seat map: #seats_container directly
// for step2, or the #seats-form wrapping it for the post-purchase picker
// (booking/partials/_booking__seating.html.erb) -- same controller, a
// different root element depending on context. Stimulus connects
// automatically whenever an element carrying data-controller="seat-map" is
// added to the DOM, which is what naturally replaces the old
// window.initSeatingScript() re-invocation dance (see step2_seating_fix
// memory for why that was ever needed) -- no manual re-init call needed
// here at all.
//
// Drops a few things the legacy file did that are dead weight for the
// current desktop markup specifically (not a general simplification -- the
// mobile file above still needs them for aob/flysmarter):
//   - The mobile order-summary list (.simple_price) -- desktop never has
//     that element.
//   - Per-passenger status text (#{flightNo}_{number}_{initials}) and the
//     per-leg subtotal (#{flightNo}_sum) -- neither element exists in the
//     current desktop seat-map design (legend/grid/footer only), so priceLabel/
//     seatLabel are gone too, they were only used to build that text.
export default class extends Controller {
  static targets = ["leg", "prevBtn", "nextBtn", "doneBtn", "selectedSummary", "total", "errorBanner", "scrollBox"];
  static values = {
    currency: String,
    passengers: Object,
    firstLeg: Number,
    lastLeg: Number,
    // Translated once server-side and handed down as a value (same
    // pattern as baggage_toggle_controller's showText/hideText) --  this
    // is a plain JS file with no access to Rails' _() gettext helper, and
    // there's no other JS-side i18n mechanism in this codebase. Defaults
    // to the untranslated Swedish word so a missing attribute degrades
    // gracefully instead of throwing.
    seatLabel: { type: String, default: "Sittplats" },
  };

  connect() {
    this.divider = this.currencyValue === "EUR" ? "" : " ";
    this.currency = this.currencyValue === "EUR" ? "€" : this.currencyValue;

    // A mutable per-leg pool of not-yet-seated passengers -- passengersValue
    // itself must stay untouched (Stimulus values are re-read from the DOM
    // attribute, not live JS state), so this is a deep-cloned working copy.
    this.passengerPool = JSON.parse(JSON.stringify(this.passengersValue));

    this.seatCart = {};
    Object.keys(this.passengerPool).forEach((legNo) => {
      this.seatCart[legNo] = { total: 0 };
    });

    this.currentLeg = this.firstLegValue;
    this.updateNavButtons();

    // connect() fires as soon as this element enters the DOM, which on
    // step2 is on page load -- while the seating dialog is still
    // display:none (only opened later via #toggle_seating_link), so every
    // scrollBox reads clientWidth 0 here and fitToContainer would have
    // nothing real to measure yet. A ResizeObserver instead fires the
    // moment each box actually gets real dimensions -- whether that's the
    // dialog opening for the first time, switching legs (each leg has its
    // own scrollBox), or a later window/orientation resize -- so it's
    // correct regardless of when/how visibility changes, with no need to
    // coordinate with whatever controller owns opening the dialog.
    this.resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const legEl = entry.target.closest('[data-seat-map-target="leg"]');
        if (legEl) this.fitToContainer(legEl);
      });
    });
    this.scrollBoxTargets.forEach((box) => this.resizeObserver.observe(box));
  }

  disconnect() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  selectSeat(event) {
    const seatEl = event.currentTarget;
    const flightNo = seatEl.dataset.flightNo;

    try {
      if (seatEl.seatingPassenger) {
        this.unassignSeat(seatEl, seatEl.seatingPassenger, true);
      } else if (this.passengerPool[flightNo] && this.passengerPool[flightNo].length > 0) {
        const passenger = this.passengerPool[flightNo][0];
        if (this.isValidSeatForPassenger(seatEl, passenger, flightNo)) {
          this.passengerPool[flightNo].shift();
          this.assignSeat(seatEl, passenger, flightNo);
        }
      } else if (!this.moveSinglePassenger(flightNo, seatEl)) {
        console.log("All passengers have selected seats or cannot move the passenger.");
      }
    } catch (error) {
      console.error("Error in seat selection:", error);
    }
  }

  goNext() {
    this.showLeg(this.nextLegWithSeats(this.currentLeg, 1));
    return false;
  }

  goPrev() {
    this.showLeg(this.nextLegWithSeats(this.currentLeg, -1));
    return false;
  }

  // #seats-form only exists on the post-purchase "Förboka sittplatser"
  // picker -- wired via data-action="submit->seat-map#validateSubmit" on
  // that form tag only, so this never fires on step2 (checkboxes there
  // submit as part of the bigger passenger-info form instead).
  validateSubmit(event) {
    const checked = this.element.querySelectorAll('input[type="checkbox"]:checked').length;
    if (checked < 1) {
      event.preventDefault();
      const banner = document.getElementById("seating-error");
      if (banner) {
        banner.classList.remove("hidden");
        setTimeout(() => banner.classList.add("hidden"), 2500);
      }
    }
  }

  // --- internals ---

  formatPrice(price) {
    if (typeof price !== "number") price = parseFloat(price) || 0;
    return this.currency === "CHF"
      ? `${this.currency}${this.divider}${price.toFixed(2)}`
      : `${price.toFixed(2)}${this.divider}${this.currency}`;
  }

  seatTextElement(seatEl) {
    return document.getElementById(`${seatEl.id}_text`);
  }

  assignSeat(seatEl, passenger, flightNo) {
    const checkbox = seatEl.querySelector(`[data-passenger="${passenger.number}"]`);
    if (!checkbox) {
      console.error("Checkbox not found for passenger", passenger.number);
      return false;
    }

    const price = parseFloat(checkbox.dataset.price) || 0;

    this.seatTextElement(seatEl).textContent = passenger.initials;
    seatEl.classList.add("selected-seat");
    seatEl.classList.remove("border-blue-200", "bg-blue-50");
    seatEl.classList.add("bg-primary", "border-primary", "text-white");
    seatEl.seatingPassenger = passenger;

    // id/description: step2_price_controller#recalculate reads these off
    // every checked checkbox carrying data-price to build the Prisöversikt
    // line-item list, the same way it already does for baggage/sports-
    // equipment checkboxes (see _step_1_baggage_panel.html.erb) -- this
    // checkbox's own server-rendered data only ever had
    // data-seatdescription (a different attribute, never read by that
    // controller) and no data-id at all, so a selected seat silently never
    // got its own Prisöversikt row. One id per (passenger, leg) --a
    // passenger can only hold one seat per leg, so re-assigning overwrites
    // rather than duplicating.
    checkbox.dataset.id = `seat_${passenger.number}_${flightNo}`;
    checkbox.dataset.description = `${this.seatLabelValue} ${checkbox.dataset.seatdescription} (${passenger.name})`;
    checkbox.checked = true;

    this.recalculateFlightTotal(flightNo);
    this.updateTotals();
    this.notifyPriceOverview();
    return true;
  }

  unassignSeat(seatEl, passenger, returnToPool) {
    const checkbox = seatEl.querySelector(`[data-passenger="${passenger.number}"]`);

    this.seatTextElement(seatEl).textContent = seatEl.dataset.seatLabel;
    seatEl.classList.remove("selected-seat", "bg-primary", "border-primary", "text-white");
    seatEl.classList.add("border-blue-200", "bg-blue-50");
    seatEl.seatingPassenger = null;

    if (checkbox) checkbox.checked = false;

    const flightNo = seatEl.dataset.flightNo;
    this.recalculateFlightTotal(flightNo);
    this.updateTotals();
    this.notifyPriceOverview();

    if (returnToPool) {
      this.passengerPool[flightNo] = this.passengerPool[flightNo] || [];
      this.passengerPool[flightNo].unshift(passenger);
    }

    return passenger;
  }

  // Seat selection lives inside the same big step_2_form as baggage/sports
  // equipment (data-controller="step2-submit step2-price ..." on the form
  // tag, per _booking_form.html.erb), but toggles its checkbox's .checked
  // directly rather than through a real click -- setting .checked in JS
  // never fires a "change" event on its own, so a data-action="change->
  // step2-price#recalculate" on the checkbox (baggage's own wiring)
  // wouldn't have fired here anyway. Calling the sibling controller
  // directly is the same cross-controller pattern step1_results_controller
  // .js#onFirstLoadComplete already uses to reach step1-filter.
  notifyPriceOverview() {
    const formEl = this.element.closest('[data-controller~="step2-price"]');
    if (!formEl) return;
    const priceController = this.application.getControllerForElementAndIdentifier(formEl, "step2-price");
    if (priceController) priceController.recalculate();
  }

  recalculateFlightTotal(flightNo) {
    let total = 0;
    this.element.querySelectorAll(`.seat[data-flight-no="${flightNo}"] input:checked`).forEach((el) => {
      total += parseFloat(el.dataset.price) || 0;
    });
    this.seatCart[flightNo].total = total;
  }

  updateTotals() {
    const sum = Object.keys(this.seatCart).reduce((total, key) => total + this.seatCart[key].total, 0);
    if (this.hasTotalTarget) this.totalTarget.textContent = this.formatPrice(sum);

    if (this.hasSelectedSummaryTarget) {
      const labels = Array.from(this.element.querySelectorAll(".selected-seat")).map((el) => el.dataset.seatLabel);
      this.selectedSummaryTarget.textContent = labels.length ? labels.join(", ") : "--";
    }
  }

  isValidSeatForPassenger(seatEl, passenger, flightNo) {
    if (passenger.name.includes("&") && seatEl.classList.contains("no_infant")) {
      const banner = document.getElementById(`seat-select-error-${flightNo}`);
      if (banner) {
        banner.classList.remove("hidden");
        setTimeout(() => banner.classList.add("hidden"), 2500);
      }
      return false;
    }
    const banner = document.getElementById(`seat-select-error-${flightNo}`);
    if (banner) banner.classList.add("hidden");
    return true;
  }

  findSeatByPassenger(flightNo, passengerNumber) {
    return Array.from(this.element.querySelectorAll(`.seat[data-flight-no="${flightNo}"]`)).find(
      (el) => el.seatingPassenger && el.seatingPassenger.number === passengerNumber
    );
  }

  moveSinglePassenger(flightNo, targetSeatEl) {
    const cartPassengers = Array.from(this.element.querySelectorAll(`.seat[data-flight-no="${flightNo}"].selected-seat`))
      .map((el) => el.seatingPassenger)
      .filter(Boolean);
    // Mirrors the legacy behavior exactly: only auto-moves when this leg
    // has precisely one currently-seated passenger to relocate.
    if (cartPassengers.length !== 1) return false;

    const passenger = cartPassengers[0];
    const currentSeat = this.findSeatByPassenger(flightNo, passenger.number);
    if (!currentSeat) return false;

    const movedPassenger = this.unassignSeat(currentSeat, passenger, false);
    return this.assignSeat(targetSeatEl, movedPassenger, flightNo);
  }

  updateNavButtons() {
    if (this.hasPrevBtnTarget) this.prevBtnTarget.classList.toggle("hidden", this.currentLeg <= this.firstLegValue);
    if (this.hasNextBtnTarget) this.nextBtnTarget.classList.toggle("hidden", this.currentLeg >= this.lastLegValue);
    if (this.hasDoneBtnTarget) this.doneBtnTarget.classList.toggle("hidden", this.currentLeg < this.lastLegValue);
  }

  legIndex(el) {
    return parseInt(el.id.replace("flight_index_", ""), 10);
  }

  nextLegWithSeats(fromLeg, direction) {
    const candidates = this.legTargets
      .map((el) => this.legIndex(el))
      .filter((idx) => (direction > 0 ? idx > fromLeg : idx < fromLeg))
      .sort((a, b) => (direction > 0 ? a - b : b - a));
    return candidates.length ? candidates[0] : fromLeg;
  }

  showLeg(legIndex) {
    if (legIndex === this.currentLeg) return;
    this.legTargets.forEach((el) => {
      el.style.display = this.legIndex(el) === legIndex ? "" : "none";
    });
    this.currentLeg = legIndex;
    this.updateNavButtons();
    this.fitToContainer(this.currentLegEl());
  }

  currentLegEl() {
    return this.legTargets.find((el) => this.legIndex(el) === this.currentLeg);
  }

  // Each seat/label/aisle-gap width and the row gap are CSS custom
  // properties (see _step2_booking_form__seating.html.erb's own comment on
  // the scrollBox div) defaulting to the original fixed rem sizes. Measure
  // this leg's natural (unscaled) content width against its actual
  // available container width and, only if it doesn't fit, scale all four
  // proportionally down to whatever exactly fits -- down to a floor that
  // keeps a seat still tappable, past which the tiny residual overflow (an
  // unrealistically-wide layout on an unrealistically-narrow screen) is
  // left as fallback scroll rather than shrinking seats past usability.
  // Different legs can be different aircraft with different column
  // counts, so this runs per-leg (on connect for the first, on showLeg for
  // every switch after) rather than once globally.
  fitToContainer(legEl) {
    if (!legEl) return;
    const box = legEl.querySelector('[data-seat-map-target="scrollBox"]');
    if (!box) return;

    box.style.removeProperty("--seat-w");
    box.style.removeProperty("--label-w");
    box.style.removeProperty("--aisle-w");
    box.style.removeProperty("--seat-gap");

    const prevOverflowX = box.style.overflowX;
    box.style.overflowX = "visible";
    const natural = box.scrollWidth;
    const available = box.clientWidth;
    box.style.overflowX = prevOverflowX;

    if (!natural || !available || natural <= available) return;

    const REM = 16;
    const MIN_SEAT_PX = 30;
    // 0.96 safety margin: border-widths on every seat/placeholder element
    // don't scale down with --seat-w, so summed across a whole row that
    // fixed per-element overhead makes the actual rendered width a little
    // wider than a pure linear scale of the box-only sizes predicts
    // (confirmed live: an unpadded ratio left ~2% still overflowing).
    const scale = Math.max((available / natural) * 0.96, MIN_SEAT_PX / (3 * REM));

    box.style.setProperty("--seat-w", `${3 * REM * scale}px`);
    box.style.setProperty("--label-w", `${2 * REM * scale}px`);
    box.style.setProperty("--aisle-w", `${1 * REM * scale}px`);
    box.style.setProperty("--seat-gap", `${0.375 * REM * scale}px`);
  }
}
